import { Instagram, Star, ArrowUpRight, BadgeCheck, MessageCircle } from "lucide-react";
import { FEED, IMG, INSTAGRAM_URL, GOOGLE_URL, waLink } from "@/lib/site";
import { Reveal } from "@/lib/motion";

const GoogleG = ({ size = 22 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size}>
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
  </svg>
);

export const SocialProof = () => (
  <section id="destaque" className="relative overflow-hidden py-24 lg:py-32 bg-slate-50">
    <div className="absolute inset-0 mesh-bg opacity-70" />
    <div className="relative max-w-7xl mx-auto px-4 md:px-8">
      <Reveal className="max-w-3xl mb-16">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#55c0e0] mb-4">Instagram & Google</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#012a60] text-balance">
          Conheça nossos trabalhos e veja a opinião dos nossos clientes
        </h2>
        <p className="mt-5 text-slate-600 leading-relaxed">
          Antes de contratar, acompanhe nossos serviços realizados e veja por que somos referência em
          vidros temperados na Grande Florianópolis.
        </p>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* Instagram phone mockup */}
        <Reveal>
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <div className="relative mx-auto w-[260px] shrink-0">
              <div className="relative aspect-[9/19] rounded-[2.4rem] bg-[#012a60] p-2.5 shadow-[0_30px_70px_-20px_rgba(1,42,96,0.6)]">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-4 w-16 rounded-full bg-[#012a60] z-20" />
                <div className="relative h-full w-full overflow-hidden rounded-[1.9rem] bg-white">
                  {/* ig header */}
                  <div className="flex items-center gap-2 px-3 pt-5 pb-2.5 border-b border-slate-100 bg-white relative z-10">
                    <div className="grid place-items-center h-8 w-8 rounded-full bg-gradient-to-tr from-[#f5b301] via-[#e1306c] to-[#833ab4]">
                      <img src={IMG.logo} alt="Gabibox" className="h-7 w-7 rounded-full object-cover ring-2 ring-white" />
                    </div>
                    <div className="leading-tight">
                      <p className="text-[11px] font-bold text-[#012a60]">vidracariagabibox</p>
                      <p className="text-[9px] text-slate-400">Grande Florianópolis</p>
                    </div>
                    <Instagram size={16} className="ml-auto text-[#e1306c]" />
                  </div>
                  {/* vertical marquee feed */}
                  <div className="relative h-full overflow-hidden">
                    <div className="grid grid-cols-2 gap-0.5 animate-marquee-y">
                      {[...FEED, ...FEED].map((src, i) => (
                        <div key={i} className="aspect-square overflow-hidden">
                          <img src={src} alt="Instalação Gabibox" className="h-full w-full object-cover" />
                        </div>
                      ))}
                    </div>
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white to-transparent" />
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center sm:text-left">
              <Instagram className="mx-auto sm:mx-0 text-[#e1306c] mb-4" size={30} />
              <h3 className="font-display text-2xl font-bold text-[#012a60]">Siga @vidracariagabibox</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-xs">
                Acompanhe diariamente nossos projetos, instalações de box, espelhos, fachadas, portas de
                vidro e muito mais.
              </p>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-tr from-[#f5b301] via-[#e1306c] to-[#833ab4] px-6 py-3 font-bold text-white hover:opacity-90 transition-opacity"
              >
                <Instagram size={17} /> Ver nosso Instagram
              </a>
            </div>
          </div>
        </Reveal>

        {/* Google card */}
        <Reveal delay={0.1}>
          <div className="rounded-3xl bg-white border border-slate-200 p-8 shadow-[0_20px_60px_-25px_rgba(1,42,96,0.3)]">
            <div className="flex items-center gap-3 mb-6">
              <GoogleG size={30} />
              <div>
                <p className="font-display text-lg font-bold text-[#012a60] flex items-center gap-1.5">
                  Vidraçaria Gabibox <BadgeCheck size={16} className="text-[#4285F4]" />
                </p>
                <p className="text-xs text-slate-400">Google Meu Negócio</p>
              </div>
            </div>

            <div className="flex items-center gap-4 rounded-2xl bg-slate-50 border border-slate-100 p-5">
              <div className="text-center">
                <p className="font-display text-5xl font-black text-[#012a60] leading-none">5.0</p>
              </div>
              <div>
                <div className="flex gap-0.5 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="fill-[#f5b301] text-[#f5b301]" />
                  ))}
                </div>
                <p className="text-sm text-slate-600">Empresa <strong className="text-[#012a60]">5 estrelas</strong> no Google</p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-4 gap-2">
              {[IMG.boxDourado, IMG.boxPreto, IMG.mesas, IMG.estoque].map((src, i) => (
                <div key={i} className="aspect-square overflow-hidden rounded-xl ring-1 ring-black/5">
                  <img src={src} alt="Foto do perfil Gabibox" className="h-full w-full object-cover" />
                </div>
              ))}
            </div>

            <p className="mt-5 text-sm text-slate-600 leading-relaxed">
              Confira as avaliações reais de nossos clientes e veja por que somos referência em qualidade
              e atendimento.
            </p>

            <a
              href={GOOGLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-[#012a60] px-6 py-3 font-bold text-[#012a60] hover:bg-[#012a60] hover:text-white transition-colors"
            >
              <Star size={16} className="fill-current" /> Ver Avaliações no Google
            </a>
          </div>
        </Reveal>
      </div>

      {/* highlight + CTA */}
      <Reveal className="mt-20 text-center max-w-3xl mx-auto">
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#012a60]">Confiança construída com qualidade</h3>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Nossa maior satisfação é entregar serviços de excelência e receber o reconhecimento dos nossos
          clientes. Veja nossos projetos no Instagram e confira as avaliações no Google antes de solicitar
          seu orçamento.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="social-whatsapp-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-bold text-white hover:opacity-90 transition-opacity">
            <MessageCircle size={17} /> Falar no WhatsApp
          </a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#012a60] px-6 py-3 font-bold text-white hover:bg-[#06356f] transition-colors">
            <Instagram size={17} /> Instagram
          </a>
          <a href={GOOGLE_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#012a60] px-6 py-3 font-bold text-[#012a60] hover:bg-[#012a60] hover:text-white transition-colors">
            <Star size={16} className="fill-current" /> Google
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);
